import { useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from 'react';

import { Button } from '../../../../../components/ui/Button';
import { Dialog } from '../../../../../components/ui/Dialog';
import { Icon } from '../../../../../components/ui/Icon';
import { Link } from '../../../../../components/ui/Link';
import { Message, MessageStack, type MessageType } from '../../../../../components/ui/Message';
import { Popconfirm } from '../../../../../components/ui/Popconfirm';
import { OrderPreviewDialog } from './OrderPreviewDialog';
import { clearPreviewDraft, draftKeyForRecord, loadImportRecords, saveImportRecords } from './order-import-drafts';
import { buildPreviewOrders } from './order-preview-fixtures';
import { Table, type TableColumn } from '../../../../../components/ui/Table';
import flowSrc from '../assets/data-import-flow.png';
import templateIconSrc from '../assets/import-template-icon.png';
import uploadIconSrc from '../assets/import-upload-icon.png';
import { buildImportRecords, deriveImportProgress, deriveImportStatus, type ImportRecord } from './fixtures';
import styles from './DataImportPanel.module.css';

export interface DataImportPanelProps {
  /** 导入后数据最终呈现的位置，写进顶部提示条。 */
  readonly modulePath: string;
  /** 模板文件名前缀，如「订单导入模板」。 */
  readonly templatePrefix: string;
}

interface ExcelFileHandle {
  getFile: () => Promise<File>;
}

interface NativeFilePickerWindow extends Window {
  showOpenFilePicker?: (options: {
    multiple: false;
    startIn: 'downloads';
    types: readonly [{
      accept: {
        'application/vnd.ms-excel': readonly ['.xls'];
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': readonly ['.xlsx'];
      };
      description: string;
    }];
  }) => Promise<readonly ExcelFileHandle[]>;
}

/*
 * 数据导入功能页的通用形态：提示条 → 导入须知（含关系图）→ 导入数据 → 导入记录列表。
 * 目前只有「订单导入」接入，其余二级菜单仍是占位；接入只需再传一份 props。
 *
 * 演示阶段列表数据由 `useState` 持有，撤销直接在本地移除；真实接口接入后替换数据源即可。
 *
 * 文件态持久化：选中的文件名写进 sessionStorage，刷新后上传位置仍显示该文件 + 「导入」入口，
 * 直到用户在预览里确认导入才清空。真实实现应换成服务端的「已上传未处理」任务记录。
 */
const PENDING_FILE_KEY = 'orderImport.pendingFile';
const UPLOADED_KEY = 'orderImport.uploaded';

/** 当前时刻格式化为 `YYYY-MM-DD HH:mm:ss`，给新落的待导入记录写「导入时间」。 */
function nowString() {
  const date = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export function DataImportPanel({ modulePath, templatePrefix }: DataImportPanelProps) {
  const scope = JSON.stringify([modulePath, templatePrefix]);
  const [records, setRecords] = useState<readonly ImportRecord[]>(() => loadImportRecords(scope, buildImportRecords(templatePrefix)));
  const previousRecords = useRef(records);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [fileName, setFileName] = useState(() => sessionStorage.getItem(PENDING_FILE_KEY) ?? '');
  const [isValidating, setValidating] = useState(false);
  const validateTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [dragging, setDragging] = useState(false);
  const [flowOpen, setFlowOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  // 当前正在「导入数据 / 确认导入」的待导入记录 id；null 表示抽屉关闭或走普通预览。
  const [activeRecordId, setActiveRecordId] = useState<number | null>(null);
  // 新增记录的 id 自增序列，从种子最大 id 之后接上，避免与演示种子撞 id。
  const [recordSeq, setRecordSeq] = useState(() => records.reduce((max, record) => Math.max(max, record.id), 0) + 1);
  // 点「导入」后两阶段 loading：上传中(2s) → 校验中(3s)，跑完开预览抽屉并落一条待导入记录。
  // 上传完未校验（中断/网络问题）再点导入：跳过上传直接校验 3s。
  const [loadingPhase, setLoadingPhase] = useState<'uploading' | 'validating' | null>(null);
  // 文件是否已上传到服务端：上传 2s 跑完置 true 并写 sessionStorage（真实产品服务端有记录，刷新不丢）。
  // 选新文件 / 删除 / 确认导入时重置。
  const [hasUploaded, setHasUploaded] = useState(() => sessionStorage.getItem(UPLOADED_KEY) === '1');
  const checkTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [notice, setNotice] = useState<{ message: string; type: MessageType } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (previousRecords.current === records) return;
    previousRecords.current = records;
    if (!saveImportRecords(scope, records)) {
      const feedback = setTimeout(() => setNotice({ message: '导入记录保存失败，刷新后可能无法恢复新任务，请稍后重试', type: 'danger' }), 0);
      return () => clearTimeout(feedback);
    }
  }, [records, scope]);

  function notify(message: string, type: MessageType = 'info') {
    setNotice({ message, type });
  }

  // 基础数据统计（订单数 / 明细项数 / 待修正项数）：演示数据由 buildPreviewOrders 程序化生成，固定。
  // 真实实现应来自服务端校验结果；useMemo 依赖空数组只算一次，避免每次渲染重算 1200 组。
  const previewStats = useMemo(() => {
    const seed = buildPreviewOrders();
    return {
      orderCount: seed.length,
      itemCount: seed.reduce((count, order) => count + order.items.length, 0),
      errorCount: seed.reduce((count, order) => count + order.errors.length, 0),
    };
  }, []);

  function takeFile(file: File | undefined) {
    if (!file) return;
    if (!/\.xlsx?$/i.test(file.name)) {
      notify('请上传需求模板格式的 Excel 文件（.xls 或 .xlsx）', 'warning');
      return;
    }
    // 选完文件先进「校验中」态：对应真实产品的「上传 → 后台校验 → 给出异常数据」链路。
    // 演示态用 setTimeout 模拟，不做带百分比的假进度条（盯着假进度跑完以为真在解析，比不显示更糟）。
    // 校验期间清掉旧文件态（含已上传/已校验标记），上传位置显示 spinner；校验完才写 fileName + sessionStorage。
    clearAllFileState();
    setValidating(true);
    if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
    validateTimerRef.current = setTimeout(() => {
      validateTimerRef.current = undefined;
      setValidating(false);
      setFileName(file.name);
      sessionStorage.setItem(PENDING_FILE_KEY, file.name);
    }, 600);
  }

  // 组件卸载时清掉残留的校验定时器，避免 setState 打到已卸载组件。
  useEffect(() => () => {
    if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
    if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
  }, []);

  // 清空全部文件态：上传位置回「选择文件」+ 重置已上传标记 + 清两个 sessionStorage。
  // 确认导入 / 删除文件 / 选新文件 / 预览抽屉打开时（落待导入记录后）调。
  function clearAllFileState() {
    setFileName('');
    setHasUploaded(false);
    sessionStorage.removeItem(PENDING_FILE_KEY);
    sessionStorage.removeItem(UPLOADED_KEY);
  }

  // 点文件图标右上角的删除角标：清空已选文件回到未上传态，同步重置已上传/已校验标记，
  // 否则文件删了按钮还带着基础数据，状态断裂。
  // 校验中（isValidating）/ 导入loading中（loadingPhase）禁用，避免与异步流程打架。
  function removeFile() {
    clearAllFileState();
  }

  // 校验跑完、文件还在手：先按当前文件名落一条「待导入」记录，再开预览抽屉并清空上传区。
  // 记录先于清文件创建，fileName 才能带进记录；清文件后上方回「选择文件」，导入按钮重新置灰。
  function openPreviewForUpload(name: string) {
    const pendingId = addPendingRecord(name);
    setActiveRecordId(pendingId);
    setPreviewOpen(true);
    clearAllFileState();
  }

  // 点「导入」两分支：
  //  - hasUploaded（上传完未校验，如中断/网络问题）：跳过上传，直接校验 1.5s
  //  - 首次：上传 1.5s → 校验 1.5s（仅为演示时长，真实请求以响应为准）
  // 两种分支终点都是 openPreviewForUpload：校验完才开抽屉 + 落待导入记录 + 清文件。
  function handleCheckAndImport() {
    if (fileName === '' || loadingPhase !== null) return;
    const currentName = fileName;
    if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
    if (hasUploaded) {
      setLoadingPhase('validating');
      checkTimerRef.current = setTimeout(() => {
        checkTimerRef.current = undefined;
        setLoadingPhase(null);
        openPreviewForUpload(currentName);
      }, 1500);
      return;
    }
    setLoadingPhase('uploading');
    checkTimerRef.current = setTimeout(() => {
      setHasUploaded(true);
      sessionStorage.setItem(UPLOADED_KEY, '1');
      setLoadingPhase('validating');
      checkTimerRef.current = setTimeout(() => {
        checkTimerRef.current = undefined;
        setLoadingPhase(null);
        openPreviewForUpload(currentName);
      }, 1500);
    }, 1500);
  }

  function onFileChange(event: ChangeEvent<HTMLInputElement>) {
    takeFile(event.currentTarget.files?.[0]);
    event.currentTarget.value = '';
  }

  async function chooseFile() {
    const nativePicker = (window as NativeFilePickerWindow).showOpenFilePicker;
    if (!nativePicker) {
      fileInputRef.current?.click();
      return;
    }

    try {
      const [handle] = await nativePicker({
        multiple: false,
        startIn: 'downloads',
        types: [{
          accept: {
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
          },
          description: 'Excel 工作簿',
        }],
      });
      if (handle) takeFile(await handle.getFile());
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      fileInputRef.current?.click();
    }
  }

  async function chooseDemoFile() {
    try {
      const response = await fetch('/downloads/order-import/订单导入案例.xlsx');
      if (!response.ok) throw new Error(`演示文件加载失败：${response.status}`);
      const demoFile = new File(
        [await response.blob()],
        '订单导入案例.xlsx',
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      );
      takeFile(demoFile);
    } catch {
      notify('演示案例文件暂时无法加载，请稍后重试', 'danger');
    }
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    takeFile(event.dataTransfer.files?.[0]);
  }

  // 撤销：把这一次导入的内容作废（演示态直接从列表移除）。
  function revokeRecord(record: ImportRecord) {
    clearPreviewDraft(draftKeyForRecord(scope, record));
    setRecords((current) => current.filter((candidate) => candidate.id !== record.id));
    notify(`已撤销「${record.fileName}」的导入记录`, 'success');
  }

  // 删除：丢弃这条还没确认导入的待导入任务。任务本来就是「上方那个已上传文件」的服务端记录，
  // 所以连同已上传的文件态一起清空回默认，否则文件还挂着、列表里却没有对应任务了。
  function deletePendingRecord(record: ImportRecord) {
    clearPreviewDraft(draftKeyForRecord(scope, record));
    setRecords((current) => current.filter((candidate) => candidate.id !== record.id));
    clearAllFileState();
    notify(`已删除待导入任务「${record.fileName}」`, 'success');
  }

  // 按当前上传文件名落一条「待导入」记录（confirmed=false），总数取演示基础数据的订单数；
  // 返回新记录 id，供调用方把预览抽屉绑定到这条记录（确认导入时再把状态翻成「已导入」）。
  function addPendingRecord(name: string): number {
    const id = recordSeq;
    const record: ImportRecord = {
      confirmed: false,
      failed: 0,
      fileName: name,
      id,
      operator: '当前用户',
      succeeded: 0,
      total: previewStats.orderCount,
      uploadedAt: nowString(),
    };
    setRecords((current) => [record, ...current]);
    setRecordSeq((next) => next + 1);
    return id;
  }

  // 点「待导入」记录的「导入数据」：文件已在服务端（已上传未处理），直接开预览抽屉复用这条记录，
  // 不再走上传 / 校验 loading。
  function openPreviewForRecord(record: ImportRecord) {
    setActiveRecordId(record.id);
    setPreviewOpen(true);
  }

  const columns: readonly TableColumn<ImportRecord>[] = [
    { fixed: 'left', key: 'fileName', title: '文件名称', width: 280 },
    { key: 'uploadedAt', title: '导入时间', width: 200 },
    { align: 'right', key: 'total', title: '总数据(条)', width: 88 },
    { align: 'right', key: 'succeeded', title: '导入成功(条)', render: (row) => <span className={styles.succeeded}>{row.succeeded}</span>, width: 96 },
    { align: 'right', key: 'failed', title: '导入失败(条)', render: (row) => <span className={styles.failed}>{row.failed}</span>, width: 96 },
    {
      key: 'status',
      render: (row) => {
        const status = deriveImportStatus(row);
        // 导入中不配圆点：进度条本身就在说「在跑」，再点一个圆点是重复信息。
        // 百分比由计数派生；「导入中」三个字只给屏幕阅读器，避免状态列又长又挤。
        if (status === '导入中') {
          const progress = deriveImportProgress(row);
          return <span className={styles.statusProgress}>
            <span aria-hidden="true" className={styles.progressTrack}>
              <span className={styles.progressBar} style={{ width: `${progress}%` }} />
            </span>
            <span aria-hidden="true" className={styles.progressText}>{progress}%</span>
            <span className={styles.visuallyHidden}>导入中</span>
          </span>;
        }
        return <span className={styles.statusText}>
          <span
            aria-hidden="true"
            className={status === '已导入' ? styles.statusDotDone : styles.statusDotPending}
          />
          {status}
        </span>;
      },
      title: '状态',
      width: 108,
    },
    { key: 'operator', title: '操作人', width: 90 },
    {
      fixed: 'right',
      key: 'actions',
      render: (row) => {
        const status = deriveImportStatus(row);
        // 待导入：文件已上传但还没确认导入，可继续导入数据或整条丢弃（删除会一并清空上方文件态）。
        if (status === '待导入') {
          return <span className={styles.actions}>
            <Link className={styles.brandLink} onClick={() => openPreviewForRecord(row)} type="primary">导入数据</Link>
            <Popconfirm confirmText="删除" confirmType="danger" description="删除后该记录不再显示，已上传的文件也会一并清空。" onConfirm={() => deletePendingRecord(row)} title="删除这条记录">
              <Link className={styles.brandLink} type="danger">删除</Link>
            </Popconfirm>
          </span>;
        }
        // 导入中还跑着，没有可点的操作；空 span 占位保证操作列宽度不塌。
        if (status === '导入中') return <span className={styles.actions} />;
        // 已导入：查看结果 / 撤销；失败笔暂时不处理（演示态不挂「处理失败」入口），只有查看与撤销。
        return <span className={styles.actions}>
          <Link className={styles.brandLink} onClick={() => notify('演示页面：查看结果暂未接入')} type="primary">查看结果</Link>
          <Popconfirm confirmText="撤销" confirmType="danger" description="撤销后本次导入的内容将不再保留，该记录也会移除。" onConfirm={() => revokeRecord(row)} title="撤销导入记录">
            <Link className={styles.brandLink} type="danger">撤销</Link>
          </Popconfirm>
        </span>;
      },
      title: '操作',
      width: 140,
    },
  ];

  return <>
    <MessageStack>{notice ? <Message message={notice.message} onOpenChange={(open) => { if (!open) setNotice(null); }} open type={notice.type} /> : null}</MessageStack>

    <div className={styles.root}>
      <div className={styles.notice}>
        <Icon label="提示" name="bubble" size="base" />
        <p className={styles.noticeText}>导入后，订单将呈现在【{modulePath}】模块中；</p>
        <Link className={`${styles.noticeHelp} ${styles.brandLink}`} onClick={() => notify('演示页面：帮助文档暂未接入')} type="primary">帮助文档</Link>
      </div>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>导入订单数据</h2>
        <p className={styles.importGuide}>
          导入须知：下载模板并整理数据后选择文件，点击导入生成在线预览；有错误可直接在表格中修改，全部无误后再确认导入。<Link className={`${styles.brandLink} ${styles.viewLink}`} onClick={() => setFlowOpen(true)} type="primary">查看</Link> 数据导入关系图
        </p>
        <div className={styles.importCard}>
          <div className={styles.step}>
            <h3 className={styles.stepTitle}>1、下载导入模板，填写数据信息</h3>
            <div className={styles.stepBody}>
              <img alt="" className={styles.downloadIcon} src={templateIconSrc} />
              <p className={styles.boxHint}>请下载导入模板，在模板中按提示填写您的数据</p>
              {/* 参考图里下载入口是白底按钮形态：Button 包在下载链接里，保留 download 语义。 */}
              <a download="订单导入模板.xlsx" href="/downloads/order-import/订单导入模板.xlsx">
                <Button type="default">下载导入模板</Button>
              </a>
            </div>
          </div>
          <div className={styles.step}>
            <h3 className={styles.stepTitle}>2、上传完善后的模板</h3>
            {/* 上传区自己承接拖拽：`Upload` 组件的虚线形态（dragger）尚未进设计系统，暂由页面实现。 */}
            <div
              className={styles.uploadBox}
              data-dragging={dragging || undefined}
              onDragLeave={() => setDragging(false)}
              onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
              onDrop={onDrop}
            >
              {isValidating ? <>
                <span aria-hidden="true" className={styles.spinner} />
                <p className={styles.boxHint}>正在校验文件数据，请稍候…</p>
              </> : fileName ? <>
                <span className={styles.fileIconWrap}>
                  <img alt="" className={styles.selectedFileIcon} src="/downloads/order-import/excel-file-icon.png" />
                  <button aria-label="删除已选文件" className={styles.removeFile} disabled={loadingPhase !== null || isValidating} onClick={removeFile} type="button">
                    <Icon aria-hidden="true" name="circle-close-filled" size="base" />
                  </button>
                </span>
                <p className={styles.fileName}>{fileName}</p>
                <Button onClick={chooseFile} type="default">重新选择</Button>
              </> : <>
                <img alt="" className={styles.uploadIcon} src={uploadIconSrc} />
                <p className={styles.boxHint}>下载模板并完善信息后，点击或拖拽文件到此处上传，支持上传 .xls、.xlsx 格式</p>
                <Button onClick={chooseDemoFile} type="default">选择文件</Button>
              </>}
              <input accept=".xls,.xlsx" aria-label="选择要导入的模板文件" className={styles.fileInput} onChange={onFileChange} ref={fileInputRef} type="file" />
            </div>
          </div>
          <div className={styles.importActions}>
            <Button
              disabled={fileName === '' || loadingPhase !== null}
              onClick={handleCheckAndImport}
              type="primary"
            >
              {loadingPhase ? (
                <><span aria-hidden="true" className={styles.btnSpinner} />{loadingPhase === 'uploading' ? '上传中' : '校验中'}</>
              ) : '导入'}
            </Button>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>导入记录列表</h2>
        <Table
          ariaLabel="导入记录列表"
          columns={columns}
          pagination={{
            onPageChange: setPage,
            onPageSizeChange: (size) => { setPageSize(size); setPage(1); },
            page,
            pageSize,
            pageSizeOptions: [10, 20, 50],
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: true,
          }}
          rowKey={(row) => row.id}
          rows={records}
        />
      </section>
    </div>

    <Dialog
      footer={<Button onClick={() => setFlowOpen(false)} type="primary">关闭</Button>}
      onOpenChange={setFlowOpen}
      open={flowOpen}
      title="数据导入关系图"
      width={1080}
    >
      <img alt="数据导入关系图：基础数据与需要前置数据之间的先后关系" className={styles.flowPreview} src={flowSrc} />
    </Dialog>

    {previewOpen ? <OrderPreviewDialog
      draftKey={draftKeyForRecord(scope, records.find((record) => record.id === activeRecordId)!)}
      fileName={records.find((record) => record.id === activeRecordId)?.fileName ?? fileName}
      onClose={(confirmed) => {
        const targetId = activeRecordId;
        setPreviewOpen(false);
        setActiveRecordId(null);
        // 确认导入：把这条待导入记录翻成「已导入」（成功笔 = 总数、失败 0，演示态视为干净导入）。
        // 文件态在开抽屉落记录时已经清空，这里无需再清。
        if (targetId != null && confirmed) {
          setRecords((current) => current.map((record) => record.id === targetId
            ? { ...record, confirmed: true, succeeded: record.total, failed: 0 }
            : record));
          const target = records.find((record) => record.id === targetId);
          if (target) clearPreviewDraft(draftKeyForRecord(scope, target));
          notify(target ? `「${target.fileName}」已导入` : '已导入', 'success');
        }
      }}
      onNotice={notify}
      open={previewOpen}
    /> : null}
  </>;
}
